import React, { useEffect, useState } from 'react';
import { motion, useScroll, useTransform } from 'framer-motion';
import { useLanguage } from './LanguageContext';
import { t } from '../utils/translations';
import { ShieldAlert } from 'lucide-react';
export function HeroSection() {
  const {
    lang
  } = useLanguage();
  const text = t[lang].hero;
  const {
    scrollY
  } = useScroll();
  // Parallax effects
  const y1 = useTransform(scrollY, [0, 1000], [0, 200]);
  const opacity = useTransform(scrollY, [0, 500], [1, 0]);
  // Split headline for staggered animation
  const words = text.headline.split(' ');
  return <section id="home" className="relative h-screen min-h-[700px] flex items-center justify-center pt-20 overflow-hidden" data-id="element-343">
      {/* Background Image with Parallax */}
      <motion.div className="absolute inset-0 z-0" style={{
      y: y1
    }} data-id="element-344">
        <img src="https://cdn.magicpatterns.com/uploads/tLdDH2Co6KbkemKHNLdjCK/hf_20260422_174820_270eb1e1-1802-4bfc-93e6-eb810c1d4546.png" alt="Move Fighters Truck" className="w-full h-full object-cover object-center scale-105" loading="eager"
      // @ts-ignore — fetchpriority is valid HTML
      fetchpriority="high" decoding="async" data-id="element-345" />
        {/* Modern gradient overlay + noise texture */}
        <div className="absolute inset-0 bg-gradient-to-b from-mf-black/80 via-mf-black/60 to-mf-black/90 mix-blend-multiply" data-id="element-346"></div>
        <div className="absolute inset-0 opacity-[0.03]" style={{
        backgroundImage: 'url("data:image/svg+xml,%3Csvg viewBox=%220 0 200 200%22 xmlns=%22http://www.w3.org/2000/svg%22%3E%3Cfilter id=%22noiseFilter%22%3E%3CfeTurbulence type=%22fractalNoise%22 baseFrequency=%220.65%22 numOctaves=%223%22 stitchTiles=%22stitch%22/%3E%3C/filter%3E%3Crect width=%22100%25%22 height=%22100%25%22 filter=%22url(%23noiseFilter)%22/%3E%3C/svg%3E")'
      }} data-id="element-347"></div>
      </motion.div>

      <motion.div className="relative z-10 max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 text-center" style={{
      opacity
    }} data-id="element-348">
        {/* Glassmorphism Badge */}
        <motion.div initial={{
        opacity: 0,
        y: 20,
        scale: 0.9
      }} animate={{
        opacity: 1,
        y: 0,
        scale: 1
      }} transition={{
        duration: 0.8,
        type: 'spring',
        bounce: 0.4
      }} className="inline-flex items-center justify-center px-5 py-2 mb-8 border border-white/20 rounded-full bg-white/10 backdrop-blur-md shadow-[0_8px_32px_0_rgba(0,0,0,0.3)]" data-id="element-349">
          <ShieldAlert className="text-mf-yellow mr-2.5" size={20} data-id="element-350" />
          <span className="text-white font-bold uppercase tracking-widest text-sm drop-shadow-md" data-id="element-351">
            Florida's #1 Moving Squad
          </span>
        </motion.div>

        {/* Staggered Headline Reveal */}
        <h1 className="text-5xl md:text-7xl lg:text-8xl font-condensed font-black text-white uppercase tracking-tight leading-[1.1] mb-8 drop-shadow-2xl flex flex-wrap justify-center gap-x-4 gap-y-2" data-id="element-352">
          {words.map((word, i) => <motion.span key={i} initial={{
          opacity: 0,
          y: 40,
          rotateX: -90
        }} animate={{
          opacity: 1,
          y: 0,
          rotateX: 0
        }} transition={{
          duration: 0.8,
          delay: i * 0.1,
          type: 'spring',
          stiffness: 100,
          damping: 15
        }} className="inline-block origin-bottom" data-id="element-353">
              {word}
            </motion.span>)}
        </h1>

        <motion.p initial={{
        opacity: 0,
        y: 20
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        duration: 0.8,
        delay: 0.6
      }} className="mt-6 text-xl md:text-2xl text-gray-200 max-w-3xl mx-auto font-medium leading-relaxed drop-shadow-lg" data-id="element-354">
          {text.sub}
        </motion.p>

        <motion.div initial={{
        opacity: 0,
        y: 20,
        scale: 0.9
      }} animate={{
        opacity: 1,
        y: 0,
        scale: 1
      }} transition={{
        duration: 0.8,
        delay: 0.8,
        type: 'spring'
      }} className="mt-12 flex justify-center" data-id="element-355">
          <a href="#booking" className="group relative inline-flex items-center justify-center px-10 py-5 font-condensed font-bold text-2xl text-white uppercase tracking-widest bg-mf-red overflow-hidden rounded-xl shadow-[0_0_40px_rgba(211,47,47,0.6)] hover:shadow-[0_0_60px_rgba(211,47,47,0.8)] transition-all duration-300 transform hover:-translate-y-1" data-id="element-356">
            <span className="absolute w-0 h-0 transition-all duration-500 ease-out bg-white rounded-full group-hover:w-full group-hover:h-full group-hover:scale-150 opacity-10" data-id="element-357"></span>
            <span className="relative z-10" data-id="element-358">{text.cta}</span>
          </a>
        </motion.div>
      </motion.div>

      {/* Modern Scroll indicator */}
      <motion.div className="absolute bottom-10 left-1/2 transform -translate-x-1/2 z-10 hidden md:flex flex-col items-center" initial={{
      opacity: 0
    }} animate={{
      opacity: 1
    }} transition={{
      delay: 1.5,
      duration: 1
    }} data-id="element-359">
        <div className="w-[30px] h-[50px] border-2 border-white/30 rounded-full flex justify-center p-2 backdrop-blur-sm bg-white/5" data-id="element-360">
          <motion.div className="w-1.5 h-3 bg-mf-yellow rounded-full" animate={{
          y: [0, 16, 0],
          opacity: [1, 0.5, 1]
        }} transition={{
          repeat: Infinity,
          duration: 1.5,
          ease: 'easeInOut'
        }} data-id="element-361"></motion.div>
        </div>
      </motion.div>
    </section>;
}